
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { StoreModel } from '@/models/store';
import { get, put, post, del } from '@/services/http';
import {
  ColumnTemplate,
  DEFAULT_TEMPLATE_ID,
  shapeTemplateList,
} from '@/services/po-column-template';
import { Container, Draggable } from 'vue-smooth-dnd';
import { EventBus } from '@/eventBus';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import {
  MetafieldOwnerResource,
  metafieldDefId,
} from '@/utils/metafieldColumnKey';

interface UnifiedColumn {
  id: string;
  label: string;
  type: 'required' | 'required-optional' | 'masterdata' | 'custom' | 'metafield';
  enabled: boolean;
  order: number;
  category?: string;
  // Metafield-specific
  metafieldKey?: string;          // The config-map key (e.g. "custom.code" or "variant:custom.code")
  namespace?: string;
  metafieldOwnerResource?: MetafieldOwnerResource;
}

// Metafield definition from API
interface MetafieldDefinition {
  key: string;           // e.g., "custom.supplier_code"
  namespace: string;     // e.g., "custom"
  name: string;          // e.g., "supplier_code"
  displayName: string;   // e.g., "Supplier Code"
  type: string;          // e.g., "single_line_text_field"
  ownerResource: MetafieldOwnerResource;
}

// Metafield column config (stored in UserConfigs)
interface MetafieldColumnConfig {
  enabled: boolean;
  order: number;
  label: string;
  namespace: string;
  name: string;
  ownerResource: MetafieldOwnerResource;
}

interface ColumnConfig {
  enabled: boolean;
  order: number;
}

interface CustomColumnData {
  id: string;
  label: string;
  enabled: boolean;
  order: number;
}

// Required columns - always shown, but can be reordered
// canDisable: true means the column can be toggled off by the user
const REQUIRED_COLUMNS = [
  { id: 'req_image', label: 'Image', canDisable: true },
  { id: 'req_product_details', label: 'Product Name' },
  { id: 'req_variant', label: 'Variant' },
  { id: 'req_sku', label: 'Supplier SKU' },
  { id: 'req_quantity', label: 'Quantity' },
  { id: 'req_unit_price', label: 'Unit Price' },
  { id: 'req_total', label: 'Total' }
];

// MasterData columns - optional
// Note: received_quantity, returned_quantity, units_left are from PO items (not Shopify API)
const MASTERDATA_COLUMNS = [
  // PO Line Item Fields (from PO line items, not MasterData API)
  { id: 'note', label: 'Note', category: 'PO Line Item' },
  { id: 'received_quantity', label: 'Received Quantity', category: 'PO Line Item' },
  { id: 'returned_quantity', label: 'Returned Quantity', category: 'PO Line Item' },
  { id: 'units_left', label: 'Units Left', category: 'PO Line Item' },
  { id: 'tax_type', label: 'Tax Type', category: 'PO Line Item' },
  { id: 'tax_rate', label: 'Tax Rate (%)', category: 'PO Line Item' },
  { id: 'tax_amount', label: 'Tax Amount', category: 'PO Line Item' },
  { id: 'landed_cost', label: 'Landed Cost', category: 'PO Line Item' },
  { id: 'landed_cost_shipping_per_unit', label: 'Shipping / Unit', category: 'PO Line Item' },
  { id: 'landed_cost_adjustment_per_unit', label: 'Adjustment / Unit', category: 'PO Line Item' },
  { id: 'margin_amount', label: 'Margin', category: 'PO Line Item' },
  { id: 'margin_percent', label: 'Margin %', category: 'PO Line Item' },
  { id: 'landed_margin_amount', label: 'Landed Margin', category: 'PO Line Item' },
  { id: 'landed_margin_percent', label: 'Landed Margin %', category: 'PO Line Item' },

  // Basic Information
  { id: 'variant_id', label: 'Variant ID', category: 'Basic' },
  { id: 'product_id', label: 'Product ID', category: 'Basic' },
  { id: 'item_description', label: 'Item Description', category: 'Basic' },
  { id: 'product_barcode', label: 'Product Barcode', category: 'Basic' },
  { id: 'product_sku', label: 'SKU', category: 'Basic' },
  { id: 'vendor_name', label: 'Vendor Name', category: 'Basic' },
  { id: 'product_type', label: 'Product Type', category: 'Basic' },
  { id: 'product_status', label: 'Product Status', category: 'Basic' },

  // Pricing
  { id: 'unit_cost', label: 'Unit Cost', category: 'Pricing' },
  { id: 'selling_price', label: 'Selling Price', category: 'Pricing' },
  { id: 'retail_value', label: 'Retail Value', category: 'Pricing' },
  { id: 'actual_inventory_cost', label: 'Actual Inventory Cost', category: 'Pricing' },

  // Quantities
  { id: 'actual_inventory_quantity', label: 'Actual Inventory Quantity', category: 'Quantities' },
  { id: 'location_inventory_quantity', label: 'Stock at PO Location', category: 'Quantities' },
  { id: 'committed_quantity', label: 'Committed Quantity', category: 'Quantities' },
  { id: 'incoming_quantity', label: 'Incoming Quantity', category: 'Quantities' },
  { id: 'available_quantity', label: 'Available Quantity', category: 'Quantities' },

  // Product Attributes
  { id: 'total_weight', label: 'Total Weight', category: 'Attributes' },
  { id: 'weight_unit', label: 'Weight Unit', category: 'Attributes' },
  { id: 'hs_code', label: 'HS Code', category: 'Attributes' },
  { id: 'country_of_origin', label: 'Country of Origin', category: 'Attributes' },
  { id: 'inventory_tracking', label: 'Inventory Tracking', category: 'Attributes' },
  { id: 'continue_selling_out_stock', label: 'Continue Selling Out of Stock', category: 'Attributes' },

  // Metadata
  { id: 'product_tags', label: 'Product Tags', category: 'Metadata' },
  { id: 'product_collections', label: 'Product Collections', category: 'Metadata' },
  { id: 'product_published_date', label: 'Product Published Date', category: 'Metadata' },
];

@Component({
  components: {
    Container,
    Draggable,
    CopyLinkButton
  }
})
export default class PurchaseOrderLineItemColumns extends Vue {
  @Prop({ type: Object, required: true }) store!: StoreModel;

  // State
  private loading = false;
  private saving = false;
  private hasChanges = false;

  // Column configs (stores order and enabled state)
  private columnConfigs: { [key: string]: ColumnConfig } = {};

  // Custom columns from API
  private customColumns: CustomColumnData[] = [];

  // Phase 3: Metafield columns
  private metafieldDefinitions: MetafieldDefinition[] = [];
  private metafieldColumns: { [key: string]: MetafieldColumnConfig } = {};
  private loadingMetafields = false;
  private metafieldError: string | null = null;

  // US-069: Per-template column configuration. The editor edits the master
  // (Default) template by default; selecting an extra template re-points the
  // same editor at that template's MasterData + metafield column payload.
  // Custom columns remain global (managed only under Default).
  private columnTemplates: ColumnTemplate[] = [];
  private selectedTemplateId: string = DEFAULT_TEMPLATE_ID;
  private templateDialog = false;
  private templateDialogMode: 'create' | 'rename' = 'create';
  private templateNameInput = '';
  private savingTemplate = false;
  private deleteTemplateDialog = false;
  private deletingTemplate = false;

  // Custom column dialog state
  private loadingCustomColumns = false;
  private savingCustomColumn = false;
  private deletingCustomColumn = false;
  private customColumnDialog = false;
  private deleteDialog = false;
  private isEditingCustomColumn = false;
  private editingColumn: CustomColumnData | null = null;
  private deletingColumn: CustomColumnData | null = null;
  private customColumnFormValid = false;
  private customColumnFormData = {
    label: '',
    enabled: true
  };

  // Computed: unified list of all columns sorted by order
  get unifiedColumns(): UnifiedColumn[] {
    const allColumns: UnifiedColumn[] = [];

    // Add required columns
    REQUIRED_COLUMNS.forEach((col, index) => {
      const config = this.columnConfigs[col.id];
      const canDisable = (col as any).canDisable === true;
      allColumns.push({
        id: col.id,
        label: col.label,
        type: canDisable ? 'required-optional' : 'required',
        enabled: canDisable ? (config?.enabled ?? true) : true,
        order: config?.order ?? (index + 1)
      });
    });

    // Add MasterData columns
    MASTERDATA_COLUMNS.forEach((col, index) => {
      const config = this.columnConfigs[col.id];
      allColumns.push({
        id: col.id,
        label: col.label,
        type: 'masterdata',
        enabled: config?.enabled ?? false,
        order: config?.order ?? (REQUIRED_COLUMNS.length + index + 1),
        category: col.category
      });
    });

    // Add custom columns
    this.customColumns.forEach(col => {
      allColumns.push({
        id: col.id,
        label: col.label,
        type: 'custom',
        enabled: col.enabled,
        order: col.order
      });
    });

    // Add metafield columns (Phase 3) — keyed by ownerResource-aware config key
    // so a product and a variant metafield with the same namespace.name don't collide.
    this.metafieldDefinitions.forEach((def) => {
      const configKey = metafieldDefId(def);
      const config = this.metafieldColumns[configKey];
      allColumns.push({
        id: `metafield_${configKey}`,
        label: config?.label || def.displayName,
        type: 'metafield',
        enabled: config?.enabled ?? false,
        order: config?.order ?? 200,
        metafieldKey: configKey,
        namespace: def.namespace,
        metafieldOwnerResource: def.ownerResource,
      });
    });

    // Sort by order
    return allColumns.sort((a, b) => a.order - b.order);
  }

  get selectedColumnsCount(): number {
    return this.unifiedColumns.filter(col => col.enabled).length;
  }

  get labelRules() {
    return [
      (v: string) => !!v || 'Label is required',
      (v: string) => (v && v.length <= 50) || 'Label must be 50 characters or less',
      (v: string) => this.isLabelUnique(v) || 'A column with this label already exists'
    ];
  }

  private isLabelUnique(label: string): boolean {
    if (!label) return true;
    const normalizedLabel = label.trim().toLowerCase();
    return !this.customColumns.some(col =>
      col.id !== this.editingColumn?.id &&
      col.label.toLowerCase() === normalizedLabel
    );
  }

  private getDefaultConfigs(): { [key: string]: ColumnConfig } {
    const defaults: { [key: string]: ColumnConfig } = {};

    // Columns that should be enabled by default.
    // AS-10746: the tax trio belongs here. Omitting it meant that the first
    // time a merchant saved this page for ANY reason — enabling an unrelated
    // column, dragging to reorder — the whole map was persisted with
    // tax_*: { enabled: false }. Those three columns are the only UI for
    // setting per-line tax, so the PO was left displaying a tax total it
    // offered no way to edit. Keep in step with the backend's
    // getDefaultLineItemColumnConfig().
    const enabledByDefault = [
      'received_quantity', 'returned_quantity', 'units_left',
      'tax_type', 'tax_rate', 'tax_amount',
    ];

    // Required columns: order 1-5 (after removing req_note)
    REQUIRED_COLUMNS.forEach((col, index) => {
      defaults[col.id] = {
        enabled: true,
        order: index + 1
      };
    });

    // MasterData columns: order 6+
    MASTERDATA_COLUMNS.forEach((col, index) => {
      defaults[col.id] = {
        enabled: enabledByDefault.includes(col.id),
        order: REQUIRED_COLUMNS.length + index + 1
      };
    });

    return defaults;
  }

  created() {
    this.columnConfigs = this.getDefaultConfigs();
  }

  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.fetchColumnConfig(),
        this.fetchColumnTemplates(),  // US-069: extra templates for the selector
        this.fetchCustomColumns(),
        this.fetchMetafieldDefinitions()
      ]);
    } finally {
      this.loading = false;
    }
  }

  async fetchColumnConfig() {
    try {
      const response: any = await get('/purchase_orders/config/line-item-columns');
      if (response.data && response.data.columns) {
        this.columnConfigs = { ...this.getDefaultConfigs(), ...response.data.columns };
      }
      // Load metafield columns config (Phase 3). Backfill ownerResource on
      // legacy entries persisted before variant metafields were supported:
      // bare "namespace.name" keys are product-scoped; "variant:..." are variant.
      const rawMetafieldColumns = response.data?.metafieldColumns || {};
      const normalized: { [key: string]: MetafieldColumnConfig } = {};
      Object.entries(rawMetafieldColumns).forEach(([key, raw]: [string, any]) => {
        const ownerResource: MetafieldOwnerResource = raw?.ownerResource
          || (key.startsWith('variant:') ? 'PRODUCTVARIANT' : 'PRODUCT');
        normalized[key] = {
          enabled: !!raw?.enabled,
          order: raw?.order ?? 200,
          label: raw?.label ?? '',
          namespace: raw?.namespace ?? '',
          name: raw?.name ?? '',
          ownerResource,
        };
      });
      this.metafieldColumns = normalized;
    } catch (error) {
      console.error('Failed to load column configuration:', error);
      this.metafieldColumns = {};
    }
  }

  /**
   * Fetch available metafield definitions from backend (Phase 3).
   *
   * The backend exposes definitions per ownerResource (PRODUCT, PRODUCTVARIANT),
   * so we fetch both in parallel and tag each entry with its resource before
   * merging into a single list.
   */
  async fetchMetafieldDefinitions() {
    this.loadingMetafields = true;
    this.metafieldError = null;

    const fetchForResource = async (
      ownerResource: MetafieldOwnerResource
    ): Promise<MetafieldDefinition[]> => {
      const response: any = await get(
        `/purchase_orders/config/metafield-definitions?ownerResource=${ownerResource}`
      );
      const items: any[] = response.data?.metafields || [];
      return items.map((m) => ({
        key: m.key,
        namespace: m.namespace,
        name: m.name,
        displayName: m.displayName,
        type: m.type,
        ownerResource,
      }));
    };

    try {
      const [productDefs, variantDefs] = await Promise.all([
        fetchForResource('PRODUCT'),
        fetchForResource('PRODUCTVARIANT'),
      ]);
      this.metafieldDefinitions = [...productDefs, ...variantDefs];
    } catch (error: any) {
      console.error('Failed to fetch metafield definitions:', error);
      this.metafieldError = 'Failed to load metafield columns';
      this.metafieldDefinitions = [];
    } finally {
      this.loadingMetafields = false;
    }
  }

  async fetchCustomColumns() {
    this.loadingCustomColumns = true;
    try {
      const response: any = await get('/purchase_orders/config/custom-columns');
      const data = response.data;

      this.customColumns = Object.entries(data.columns || {}).map(([id, col]: [string, any]) => ({
        id,
        label: col.label,
        enabled: col.enabled,
        order: col.order
      })).sort((a, b) => a.order - b.order);
    } catch (error) {
      console.error('Failed to load custom columns:', error);
    } finally {
      this.loadingCustomColumns = false;
    }
  }

  onToggleMasterData(column: UnifiedColumn, enabled: boolean) {
    this.columnConfigs[column.id].enabled = enabled;
    this.hasChanges = true;
  }

  onToggleRequiredOptional(column: UnifiedColumn, enabled: boolean) {
    if (!this.columnConfigs[column.id]) {
      this.$set(this.columnConfigs, column.id, { enabled, order: column.order });
    } else {
      this.columnConfigs[column.id].enabled = enabled;
    }
    this.hasChanges = true;
  }

  async onToggleCustom(column: UnifiedColumn, enabled: boolean) {
    const customCol = this.customColumns.find(c => c.id === column.id);
    if (!customCol) return;

    const previousValue = customCol.enabled;
    customCol.enabled = enabled;

    try {
      await put(`/purchase_orders/config/custom-columns/${column.id}`, { enabled });
    } catch (error) {
      // Revert on error
      customCol.enabled = previousValue;
      EventBus.$emit('show-snackbar', { text: 'Failed to update column', color: 'error' });
    }
  }

  /**
   * Toggle metafield column enabled state (Phase 3)
   */
  onToggleMetafield(column: UnifiedColumn, enabled: boolean) {
    if (!column.metafieldKey) return;

    // Match the definition by full (ownerResource, namespace, name) — not just key —
    // so we resolve correctly when product and variant share namespace.name.
    const def = this.metafieldDefinitions.find(
      (d) => metafieldDefId(d) === column.metafieldKey
    );
    if (!def) return;

    if (!this.metafieldColumns[column.metafieldKey]) {
      // Initialize config for newly enabled metafield
      // Preserve the column's current order instead of moving to bottom
      this.metafieldColumns[column.metafieldKey] = {
        enabled: enabled,
        order: column.order,
        label: def.displayName,
        namespace: def.namespace,
        name: def.name,
        ownerResource: def.ownerResource,
      };
    } else {
      this.metafieldColumns[column.metafieldKey].enabled = enabled;
      // Backfill ownerResource for entries persisted before this field existed
      if (!this.metafieldColumns[column.metafieldKey].ownerResource) {
        this.metafieldColumns[column.metafieldKey].ownerResource = def.ownerResource;
      }
    }

    // Force reactivity update
    this.metafieldColumns = { ...this.metafieldColumns };
    this.hasChanges = true;
  }

  /**
   * Get next available order number
   */
  private getNextOrder(): number {
    const allOrders = this.unifiedColumns.map(c => c.order);
    return Math.max(...allOrders, 0) + 1;
  }

  /**
   * Get tooltip text for column category
   */
  private getCategoryTooltip(category: string): string {
    const tooltips: { [key: string]: string } = {
      'Basic': 'Basic product information from Shopify',
      'Pricing': 'Product pricing and cost data',
      'Quantities': 'Inventory quantity information',
      'Attributes': 'Product attributes like weight, HS code, etc.',
      'Metadata': 'Product tags, collections, and other metadata'
    };
    return tooltips[category] || `${category} column from Shopify product data`;
  }

  onDrop(dropResult: any) {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null || addedIndex === null || removedIndex === addedIndex) {
      return;
    }

    // Get current sorted list and reorder
    const sorted = [...this.unifiedColumns];
    const moved = sorted.splice(removedIndex, 1)[0];
    sorted.splice(addedIndex, 0, moved);

    // Update order values for all columns
    sorted.forEach((col, index) => {
      const newOrder = index + 1;

      if (col.type === 'required' || col.type === 'masterdata') {
        // Update local config
        if (!this.columnConfigs[col.id]) {
          this.columnConfigs[col.id] = { enabled: col.enabled, order: newOrder };
        } else {
          this.columnConfigs[col.id].order = newOrder;
        }
      } else if (col.type === 'custom') {
        // Update custom column order
        const customCol = this.customColumns.find(c => c.id === col.id);
        if (customCol) {
          customCol.order = newOrder;
        }
      } else if (col.type === 'metafield' && col.metafieldKey) {
        // Update metafield column order (Phase 3)
        if (this.metafieldColumns[col.metafieldKey]) {
          this.metafieldColumns[col.metafieldKey].order = newOrder;
        }
      }
    });

    // Force reactivity for metafield columns
    this.metafieldColumns = { ...this.metafieldColumns };
    this.hasChanges = true;
  }

  // Build the metafieldColumns payload (Phase 3) — emit the ownerResource-aware
  // config key the backend expects:
  //   PRODUCT          -> "<namespace>.<name>"
  //   PRODUCTVARIANT   -> "variant:<namespace>.<name>"
  private buildMetafieldColumnsPayload(): { [key: string]: MetafieldColumnConfig } {
    const payload: { [key: string]: MetafieldColumnConfig } = {};
    this.metafieldDefinitions.forEach((def) => {
      const configKey = metafieldDefId(def);
      const config = this.metafieldColumns[configKey];
      if (config) {
        const unified = this.unifiedColumns.find((u) => u.metafieldKey === configKey);
        payload[configKey] = {
          enabled: config.enabled,
          order: unified?.order ?? config.order,
          label: config.label || def.displayName,
          namespace: def.namespace,
          name: def.name,
          ownerResource: def.ownerResource,
        };
      }
    });
    return payload;
  }

  async save() {
    if (!this.hasChanges) return;

    this.saving = true;
    try {
      const metafieldColumnsToSave = this.buildMetafieldColumnsPayload();

      // US-069: route the MasterData + metafield columns to the master config
      // (Default) or the selected extra template. Custom columns stay global.
      if (this.selectedTemplateId === DEFAULT_TEMPLATE_ID) {
        await put('/purchase_orders/config/line-item-columns', {
          columns: this.columnConfigs,
          metafieldColumns: metafieldColumnsToSave
        });
      } else {
        await put(`/purchase_orders/config/column-templates/${this.selectedTemplateId}`, {
          columns: this.columnConfigs,
          metafieldColumns: metafieldColumnsToSave
        });
      }

      // Save custom column orders (global — applies regardless of template)
      await Promise.all(
        this.customColumns.map(col =>
          put(`/purchase_orders/config/custom-columns/${col.id}`, { order: col.order })
        )
      );

      // Keep the cached template list in sync with what we just saved.
      this.syncCachedTemplate(this.selectedTemplateId, this.columnConfigs, metafieldColumnsToSave);

      EventBus.$emit('show-snackbar', { text: 'Configuration saved successfully', color: 'success' });
      this.hasChanges = false;
    } catch (error) {
      console.error('Failed to save configuration:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to save configuration', color: 'error' });
    } finally {
      this.saving = false;
    }
  }

  // ========== US-069: Template manager ==========

  get isDefaultTemplate(): boolean {
    return this.selectedTemplateId === DEFAULT_TEMPLATE_ID;
  }

  async fetchColumnTemplates() {
    try {
      const response: any = await get('/purchase_orders/config/column-templates');
      this.columnTemplates = shapeTemplateList(response?.data);
    } catch (error) {
      console.error('Failed to load column templates:', error);
      this.columnTemplates = [];
    }
  }

  // Point the editor at a template's MasterData + metafield payload.
  private applyTemplateToEditor(template: ColumnTemplate) {
    this.columnConfigs = { ...this.getDefaultConfigs(), ...(template.columns || {}) };
    const normalized: { [key: string]: MetafieldColumnConfig } = {};
    Object.entries(template.metafieldColumns || {}).forEach(([key, raw]: [string, any]) => {
      const ownerResource: MetafieldOwnerResource = raw?.ownerResource
        || (key.startsWith('variant:') ? 'PRODUCTVARIANT' : 'PRODUCT');
      normalized[key] = {
        enabled: !!raw?.enabled,
        order: raw?.order ?? 200,
        label: raw?.label ?? '',
        namespace: raw?.namespace ?? '',
        name: raw?.name ?? '',
        ownerResource,
      };
    });
    this.metafieldColumns = normalized;
  }

  // Update the locally cached template entry after a save/create.
  private syncCachedTemplate(
    id: string,
    columns: { [key: string]: ColumnConfig },
    metafieldColumns: { [key: string]: MetafieldColumnConfig },
  ) {
    const idx = this.columnTemplates.findIndex(t => t.id === id);
    if (idx !== -1) {
      this.columnTemplates.splice(idx, 1, {
        ...this.columnTemplates[idx],
        columns: { ...columns },
        metafieldColumns: { ...metafieldColumns } as any,
      });
    }
  }

  onSelectTemplate(id: string) {
    if (id === this.selectedTemplateId) return;
    if (this.hasChanges && !window.confirm('Discard unsaved changes to the current template?')) {
      return;
    }
    this.selectedTemplateId = id;
    const template = this.columnTemplates.find(t => t.id === id);
    if (template) {
      this.applyTemplateToEditor(template);
    }
    this.hasChanges = false;
  }

  get templateNameValid(): boolean {
    const name = this.templateNameInput.trim();
    if (!name || name.length > 80) return false;
    if (name.toLowerCase() === 'default') return false;
    // Unique (case-insensitive), excluding the one being renamed.
    return !this.columnTemplates.some(t =>
      t.name.toLowerCase() === name.toLowerCase()
      && !(this.templateDialogMode === 'rename' && t.id === this.selectedTemplateId)
    );
  }

  openCreateTemplateDialog() {
    this.templateDialogMode = 'create';
    this.templateNameInput = '';
    this.templateDialog = true;
  }

  openRenameTemplateDialog() {
    if (this.isDefaultTemplate) return;
    const current = this.columnTemplates.find(t => t.id === this.selectedTemplateId);
    this.templateDialogMode = 'rename';
    this.templateNameInput = current?.name || '';
    this.templateDialog = true;
  }

  async submitTemplateDialog() {
    if (!this.templateNameValid) return;
    const name = this.templateNameInput.trim();
    this.savingTemplate = true;
    try {
      if (this.templateDialogMode === 'create') {
        // Duplicate the currently-edited columns into a new template.
        const response: any = await post('/purchase_orders/config/column-templates', {
          name,
          columns: this.columnConfigs,
          metafieldColumns: this.buildMetafieldColumnsPayload(),
        });
        const created = shapeTemplateList({ templates: [response.data] })[0];
        if (created) {
          this.columnTemplates.push(created);
          this.selectedTemplateId = created.id;
          this.applyTemplateToEditor(created);
          this.hasChanges = false;
        }
        EventBus.$emit('show-snackbar', { text: `Template "${name}" created`, color: 'success' });
      } else {
        await put(`/purchase_orders/config/column-templates/${this.selectedTemplateId}`, { name });
        const idx = this.columnTemplates.findIndex(t => t.id === this.selectedTemplateId);
        if (idx !== -1) {
          this.columnTemplates.splice(idx, 1, { ...this.columnTemplates[idx], name });
        }
        EventBus.$emit('show-snackbar', { text: 'Template renamed', color: 'success' });
      }
      this.templateDialog = false;
    } catch (error: any) {
      console.error('Failed to save template:', error);
      const msg = error?.response?.data?.message || 'Failed to save template';
      EventBus.$emit('show-snackbar', { text: msg, color: 'error' });
    } finally {
      this.savingTemplate = false;
    }
  }

  async deleteTemplate() {
    if (this.isDefaultTemplate) return;
    this.deletingTemplate = true;
    try {
      await del(`/purchase_orders/config/column-templates/${this.selectedTemplateId}`);
      this.columnTemplates = this.columnTemplates.filter(t => t.id !== this.selectedTemplateId);
      this.deleteTemplateDialog = false;
      // Fall back to the Default template after deletion.
      this.selectedTemplateId = DEFAULT_TEMPLATE_ID;
      const def = this.columnTemplates.find(t => t.id === DEFAULT_TEMPLATE_ID);
      if (def) this.applyTemplateToEditor(def);
      this.hasChanges = false;
      EventBus.$emit('show-snackbar', { text: 'Template deleted', color: 'success' });
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to delete template', color: 'error' });
    } finally {
      this.deletingTemplate = false;
    }
  }

  // ========== Custom Column Dialog Methods ==========

  openCreateDialog() {
    this.isEditingCustomColumn = false;
    this.editingColumn = null;
    this.customColumnFormData = {
      label: '',
      enabled: true
    };
    this.customColumnDialog = true;
  }

  openEditDialog(column: UnifiedColumn) {
    const customCol = this.customColumns.find(c => c.id === column.id);
    if (!customCol) return;

    this.isEditingCustomColumn = true;
    this.editingColumn = customCol;
    this.customColumnFormData = {
      label: customCol.label,
      enabled: customCol.enabled
    };
    this.customColumnDialog = true;
  }

  goToCustomValues(column: UnifiedColumn) {
    this.$router.push({
      name: 'custom-column-values',
      query: { column: column.id }
    });
  }

  closeCustomColumnDialog() {
    this.customColumnDialog = false;
    this.editingColumn = null;
    (this.$refs.customColumnForm as any)?.reset?.();
  }

  async saveCustomColumn() {
    if (!this.customColumnFormValid) return;

    this.savingCustomColumn = true;
    const isCreating = !this.isEditingCustomColumn;
    const newColumnLabel = this.customColumnFormData.label;

    try {
      // Save pending order changes before creating new column to prevent losing them
      if (isCreating && this.hasChanges) {
        await this.save();
      }

      if (this.isEditingCustomColumn && this.editingColumn) {
        await put(
          `/purchase_orders/config/custom-columns/${this.editingColumn.id}`,
          this.customColumnFormData
        );
        EventBus.$emit('show-snackbar', { text: 'Column updated successfully', color: 'success' });
      } else {
        await post('/purchase_orders/config/custom-columns', this.customColumnFormData);
        EventBus.$emit('show-snackbar', { text: 'Column created successfully', color: 'success' });
      }

      this.closeCustomColumnDialog();
      await this.fetchCustomColumns();

      // Auto-scroll to newly created column
      if (isCreating) {
        this.$nextTick(() => {
          // Find the newly created column by label
          const newColumn = this.customColumns.find(c => c.label === newColumnLabel);
          if (newColumn) {
            const refName = `column-${newColumn.id}`;
            const element = this.$refs[refName] as HTMLElement[] | HTMLElement | undefined;
            const targetEl = Array.isArray(element) ? element[0] : element;
            if (targetEl) {
              targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Add highlight effect
              targetEl.classList.add('highlight-new');
              setTimeout(() => targetEl.classList.remove('highlight-new'), 2000);
            }
          }
        });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save column';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.savingCustomColumn = false;
    }
  }

  confirmDelete(column: UnifiedColumn) {
    const customCol = this.customColumns.find(c => c.id === column.id);
    if (!customCol) return;

    this.deletingColumn = customCol;
    this.deleteDialog = true;
  }

  async deleteCustomColumn() {
    if (!this.deletingColumn) return;

    this.deletingCustomColumn = true;
    try {
      await del(`/purchase_orders/config/custom-columns/${this.deletingColumn.id}`);
      EventBus.$emit('show-snackbar', { text: 'Column deleted successfully', color: 'success' });
      this.deleteDialog = false;
      this.deletingColumn = null;
      await this.fetchCustomColumns();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete column';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.deletingCustomColumn = false;
    }
  }
}
